import Button from "core/buttons/button/Button";
import Commutator from "core/commutator/Commutator";
import Dropdown from "core/dropdown/Dropdown";
import DropdownLinkItem from "core/dropdown/DropdownLinkItem";
import HoverMenuButton from "./HoverMenuButton";
import PageContext from "pages/PageContext";
import _ from "lodash";
import { contentHeader } from "utils/helper";
import { FormattedMessage } from "react-intl";
import { OnClickOutside } from "utils/functions";
import { ROLE_MATCH_MODE, SCOPES } from "constants/constants";
import { ROLE_ADMIN, ROLE_RGP } from "access-roles/roles";
import { canUserPerformAction } from "access-roles/general-functions";
import { getProperSize, tabletSize } from "utils/screenSizes";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Icon from "core/icon/Icon";
import PropTypes from "prop-types";
import { useUserContext } from "api/user/UserProvider";
import { useAlertsContext } from "api/alerts/AlertsProvider";
import { useMissionContext } from "api/mission/MissionProvider";

const TopHeader = ({
  changeLanguage,
  changeUserScope,
  currentUserScope,
  navigate,
  isLangActive,
  getFullUsername,
}) => {
  const { userScope, logout, backInitialAccount } = useContext(PageContext);
  const { unreadNotificationsCount, getUnreadNotifications } =
    useAlertsContext();
  const location = useLocation();
  const { userRoleList, userScopeList, token } = useUserContext();
  const { setDisplayMission } = useMissionContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [header, setHeader] = useState([]);
  const [mobileHeaderActive, setMobileHeaderActive] = useState({
    menu: false,
    user: false,
    commutator: false,
  });
  const isAdmin = canUserPerformAction({
    userRoles: userRoleList,
    requiredRoles: [ROLE_ADMIN, ROLE_RGP],
    mode: ROLE_MATCH_MODE.OR,
  });
  const unreadNotificationsTotal =
    unreadNotificationsCount?.inbox + unreadNotificationsCount?.archived;

  const isMobileHeaderOpen = () => {
    return Object.values(mobileHeaderActive).some((e) => e);
  };

  document.body.style.overflow = isMobileHeaderOpen() ? "hidden" : "";

  useEffect(() => {
    if (userScopeList) {
      setHeader(contentHeader({ rights: userRoleList }));
    }
  }, [userScopeList, userRoleList]);

  const menuRef = useRef(null);
  const [listening, setListening] = useState(false);

  useEffect(() => {
    if (token) {
      getUnreadNotifications();
    }
  }, [token]);

  useEffect(OnClickOutside(listening, setListening, menuRef, setIsOpen));

  const dropdownMenuOpen = (headerId, option = null) => {
    const index = header.findIndex((e) => e.translationId === headerId);
    if (index >= 0 && header[index].has_dropdown) {
      setHeader(
        header.map((elm, i) => {
          if (i === index) {
            return {
              ...elm,
              active_dropdown:
                option === "close" ? false : !elm.active_dropdown,
            };
          }
          return { ...elm, active_dropdown: false };
        })
      );
    }
  };

  const dropdownMenuClose = ({ menu, path }) => {
    dropdownMenuOpen(menu.translationId);
    navigate(path.path);
    burgerMenuToDisplay("close");
  };

  const burgerMenuToDisplay = (type) => {
    const keys = Object.keys(mobileHeaderActive);
    const newHeader = {};
    if (type === "close") {
      keys.forEach((el) => {
        newHeader[el] = false;
      });
      setMobileHeaderActive(newHeader);
    } else {
      keys.forEach((el) => {
        newHeader[el] = el === type ? !mobileHeaderActive[type] : false;
      });
      setMobileHeaderActive(newHeader);
    }
    header.forEach((el) => {
      dropdownMenuOpen(el.translationId, "close");
    });
  };

  const displayLang = () => (
    <>
      <button
        className="language button-style"
        onClick={() => changeLanguage("fr")}
      >
        <a className={isLangActive("fr")}>FR</a>
      </button>
      <div className="translation-separator"></div>
      <button
        className="language button-style"
        onClick={() => changeLanguage("en")}
      >
        <a className={isLangActive("en")}>ENG</a>
      </button>
    </>
  );

  const resetStateMobileHeaderActive = () => {
    setMobileHeaderActive({
      menu: false,
      user: false,
      commutator: false,
    });
  };

  const navigateAndCloseMenu = (menu) => {
    navigate(menu.path);
    resetStateMobileHeaderActive();
  };

  return (
    <div className="top-container">
      <div className="commutator-container">
        <Commutator
          currentTab={currentUserScope || SCOPES.PERSO}
          changeTab={changeUserScope}
          className="commutator-desktop"
          id="commutator-desktop"
        />
      </div>
      <div className="menu-container">
        <ul className="custom-navbar">
          {header.map((menu) => {
            return (
              <li
                className="navbar-link"
                key={menu.translationId}
                onClick={() =>
                  menu.id === "missions" && setDisplayMission(false)
                }
              >
                <HoverMenuButton
                  defaultMessage={menu.defaultMessage}
                  has_dropdown={menu.has_dropdown}
                  menu={menu}
                  menu_index={menu.menu_index}
                  scope={userScope}
                  translationId={menu.translationId}
                />
              </li>
            );
          })}
        </ul>
      </div>
      <div className="translation-container">{displayLang()}</div>
      <div className="user-account-container">
        <button
          className="user-notification-icon button-style user-notification"
          onClick={() => navigate("notifications")}
        >
          {unreadNotificationsTotal > 0 && (
            <div className="user-notification_unread-notifications-counter">
              <span className="user-notification_unread-notifications-counter_number">
                {unreadNotificationsTotal}
              </span>
            </div>
          )}
        </button>
        <div className="user-account" ref={menuRef}>
          <Button
            className="btn-nav"
            color="white"
            id="user-account-btn"
            icon={isOpen ? "arrow-up" : "arrow-down"}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className="user-account-icon">
              <Icon
                icon="user-icon"
                color={`${mobileHeaderActive.user ? "pink" : "white"}`}
              />
            </div>
            <div className={`user-account__dropdown ${isOpen ? "active" : ""}`}>
              <Dropdown
                width={tabletSize ? "max-content" : 347}
                marginLeft={getProperSize("", "", "", "-160", "-140", "-140")}
                id="user-account-dropdown"
              >
                {isAdmin && (
                  <DropdownLinkItem
                    id="header_user_account_menu_admin_access"
                    to={`${userScope}/settings`}
                  >
                    <FormattedMessage
                      id="header_user_account_menu_admin_access"
                      defaultMessage="Aller dans panneau de configuration"
                    />
                  </DropdownLinkItem>
                )}
                {localStorage.getItem("mascarade_token") && (
                  <DropdownLinkItem
                    id="header_user_account_menu_admin_access"
                    onClick={backInitialAccount}
                  >
                    <FormattedMessage
                      id="back_original_account"
                      defaultMessage="Retour au compte initial"
                    />
                  </DropdownLinkItem>
                )}
                <DropdownLinkItem
                  id="header_user_account_menu_logout"
                  onClick={logout}
                >
                  <FormattedMessage
                    id="header_user_account_menu_logout"
                    defaultMessage="Déconnexion"
                  />
                </DropdownLinkItem>
              </Dropdown>
            </div>
          </Button>
        </div>
      </div>
      <div className="mobile-menu-container">
        <button
          className={
            mobileHeaderActive.menu
              ? "menu-trigger active button-style"
              : "menu-trigger button-style"
          }
          onClick={() => burgerMenuToDisplay("menu")}
        ></button>
        <div className="btn-group-top">
          <div className="user-notification">
            <button
              className="user-account-icon button-style"
              onClick={() => {
                setIsMobileOpen(!isMobileOpen);
                burgerMenuToDisplay("user");
              }}
            >
              <Icon
                icon="user-icon"
                color={`${mobileHeaderActive.user ? "pink" : "white"}`}
              />
            </button>
            <button
              className="user-notification-icon button-style"
              onClick={() => {
                navigate("notifications");
                resetStateMobileHeaderActive();
              }}
            >
              <Icon icon="bell" color={"white"} width={35} />
              {unreadNotificationsTotal > 0 && (
                <div className="user-notification_unread-notifications-counter">
                  <span className="user-notification_unread-notifications-counter_number">
                    {unreadNotificationsTotal}
                  </span>
                </div>
              )}
            </button>
          </div>
        </div>
        <div
          className={
            isMobileHeaderOpen() ? "menu-content active" : "menu-content"
          }
        >
          <button
            className="menu-content-close button-style"
            onClick={() => burgerMenuToDisplay("close")}
          ></button>
          <div
            className={
              mobileHeaderActive.menu
                ? "menu-content-menu active"
                : "menu-content-menu"
            }
          >
            <div className="menu-content-menu-links">
              <ul>
                {header.map((menu) => {
                  return (
                    <li
                      className="navbar-link"
                      key={`mobile-${menu.translationId}`}
                    >
                      <p
                        className={
                          _.includes(menu.path, location.pathname)
                            ? "active"
                            : ""
                        }
                        onClick={() =>
                          menu.has_dropdown
                            ? dropdownMenuOpen(menu.translationId)
                            : navigateAndCloseMenu(menu)
                        }
                      >
                        <FormattedMessage
                          id={menu.translationId}
                          defaultMessage={menu.defaultMessage}
                        />
                      </p>
                      {menu.has_dropdown && (
                        <div
                          className={`navbar-link__dropdown ${
                            menu.active_dropdown ? "active" : ""
                          }`}
                        >
                          <Dropdown id="navbar-dropdown">
                            {menu.dropdown_menu?.map((dropdown_element) => (
                              <DropdownLinkItem
                                onClick={() =>
                                  dropdownMenuClose({
                                    menu,
                                    path: dropdown_element,
                                  })
                                }
                                key={dropdown_element.translationId}
                                id={dropdown_element.translationId}
                              >
                                <FormattedMessage
                                  id={dropdown_element.translationId}
                                  defaultMessage={
                                    dropdown_element.defaultMessage
                                  }
                                />
                              </DropdownLinkItem>
                            ))}
                          </Dropdown>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
          <div
            className={
              mobileHeaderActive.user
                ? "menu-content-user active"
                : "menu-content-user"
            }
          >
            <div className="menu-content-user-account">{getFullUsername}</div>

            <Commutator
              currentTab={currentUserScope || SCOPES.PERSO}
              changeTab={changeUserScope}
              className="commutator-mobile"
              id="commutator-mobile"
            />

            <div className="menu-content-user-account-nav">
              {isAdmin && (
                <button
                  className="menu-content-user-account-nav-link button-style"
                  onClick={() => {
                    navigate("settings");
                    resetStateMobileHeaderActive();
                  }}
                >
                  <FormattedMessage
                    id="header_user_account_menu_admin_access"
                    defaultMessage="Aller dans panneau de configuration"
                  />
                </button>
              )}
              <button
                className="menu-content-user-account-nav-link button-style"
                onClick={() => {
                  resetStateMobileHeaderActive();
                  logout();
                }}
              >
                <FormattedMessage
                  id="header_user_account_menu_logout"
                  defaultMessage="Déconnexion"
                />
              </button>
              <div className="menu-content-menu-translation">
                {displayLang()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopHeader;

TopHeader.propTypes = {
  changeLanguage: PropTypes.func,
  changeUserScope: PropTypes.func,
  currentUserScope: PropTypes.string,
  navigate: PropTypes.func,
  isLangActive: PropTypes.func,
  getFullUsername: PropTypes.string,
};
